import React, { Fragment, useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import { mapCardTypeToBusinessUnitComponent } from 'components/onboard/helpers';

import { getLocalStorage, trackEvent } from '@zola-helpers/client';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import { CaretIcon } from '@zola/zola-ui/src/components/SvgIcons';

import { getPLPPhotoPreviewState } from 'cards/selectors/plpPhotoPreview';
import { updatePlpPhotoPreviewState } from 'cards/actions/plpPhotoPreview';
import { getUserContext } from 'selectors/user/userSelectors';

import openUploadcareModal from '@zola-helpers/client/dist/es/uploadcare/openUploadcareModal';

import PreviewPhotoWalkthrough from 'cards/components/CardListing/PhotoPreview/Walkthrough/PreviewPhotoWalkthrough';

import { Box, ButtonBox, DropdownBox, PhotoOption, SwapIcon, RemoveIcon, SaveIcon } from './styles';
import {
  PHOTO_PREVIEW_WALKTHROUGH_TRIGGERED,
  PLP_ADD_LIVE_PHOTO,
  PLP_PREVIEW_LIVE_PHOTO,
  ZOLA_PLP_PHOTO_PREVIEW,
} from '../constants';

/* eslint-disable  @typescript-eslint/no-explicit-any */

export interface UploadProps {
  cardType?: string;
  mobile?: boolean;
}

const Upload = ({ cardType, mobile }: UploadProps): JSX.Element | null => {
  const dispatch = useDispatch();

  const plpPhotoPreviewState = useSelector(getPLPPhotoPreviewState);
  const userContext = useSelector(getUserContext);

  const [showOptions, setShowOptions] = useState(false);
  const [showPreviewPhotoWalkthrough, setShowPreviewPhotoWalkthrough] = useState(false);

  // TODO: Move this into useState or something, need to make sure it updates properly to be used in the tracking flow though
  let flowUuid = uuidv4();
  let isFromWalkthrough = false;

  useEffect(() => {
    const hasSeenPhotoPreviewWalkthrough = getLocalStorage(PHOTO_PREVIEW_WALKTHROUGH_TRIGGERED);

    if (hasSeenPhotoPreviewWalkthrough !== 'true') {
      setShowPreviewPhotoWalkthrough(true);
    }
  }, []);

  useEffect(() => {
    const handler = (event: any) => {
      event?.stopPropagation();
      event?.nativeEvent?.stopImmediatePropagation();
      setShowOptions(false);
    };

    window.addEventListener('click', handler);
    return () => {
      window.removeEventListener('click', handler);
    };
  }, []);

  // Clear localstorage on PLP load for specific cardtype to avoid transfer of old photos to PDP if no image is uploaded
  useEffect(() => {
    if (!cardType) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [cardType]: ignored, ...sessionData } = JSON.parse(
      window.sessionStorage.getItem(ZOLA_PLP_PHOTO_PREVIEW) || '{}'
    );
    window.sessionStorage.setItem(ZOLA_PLP_PHOTO_PREVIEW, JSON.stringify(sessionData));
  }, [cardType]);

  const handleDropdownSave = () => {
    if (userContext?.is_guest) {
      const businessUnitComponent = mapCardTypeToBusinessUnitComponent(cardType as string);
      window.location.href = `/wedding/onboard/wedding-paper/${businessUnitComponent}/form`;
    }
  };

  const Dropdown = useCallback(
    ({ onSwap, onDelete, onPhotoSave }) => {
      return (
        <DropdownBox
          onClick={event => {
            event?.stopPropagation();
            event?.nativeEvent?.stopImmediatePropagation();
          }}
        >
          <PhotoOption onClick={onSwap}>
            <SwapIcon /> Swap
          </PhotoOption>
          <PhotoOption onClick={onDelete}>
            <RemoveIcon /> Delete
          </PhotoOption>
          {userContext.is_guest && (
            <PhotoOption onClick={onPhotoSave}>
              <SaveIcon /> Save
            </PhotoOption>
          )}
        </DropdownBox>
      );
    },
    [userContext]
  );

  const handleShowOptions = useCallback((show, event) => {
    event?.stopPropagation();
    event?.nativeEvent?.stopImmediatePropagation();
    setShowOptions(show);
  }, []);

  const getTrackingData = useCallback(
    () => ({
      entry_point: isFromWalkthrough ? PLP_ADD_LIVE_PHOTO : PLP_PREVIEW_LIVE_PHOTO,
      flow_run_id: flowUuid,
      business_unit: BUSINESS_UNIT.INVITATIONS,
      category: cardType,
    }),
    [cardType, flowUuid, isFromWalkthrough]
  );

  const onSave = useCallback(
    uploadcareFile => {
      if (cardType) {
        window.sessionStorage.setItem(
          ZOLA_PLP_PHOTO_PREVIEW,
          JSON.stringify({
            [cardType]: {
              UcCdnUrl: uploadcareFile.cdnUrl,
              UcMimeType: uploadcareFile.mimeType,
              flowUuid,
            },
          })
        );
        dispatch(updatePlpPhotoPreviewState({ cardType: cardType || null, uploadcareFile }));
      }
      trackEvent('Flow Completed', getTrackingData());
    },
    [dispatch, cardType, getTrackingData, flowUuid]
  );

  const uploadcareModalOptions = {
    cropRatio: '1:1 minimum',
    onUploadcareFileReady: onSave,
    analyticsLocation: '',
    analyticsProduct: 'CARDS',
  };

  const startUcFlow = () => {
    flowUuid = uuidv4();
    isFromWalkthrough = false;
    trackEvent('Flow Started', getTrackingData());
    openUploadcareModal({
      ...uploadcareModalOptions,
      additionalUploadedFilesTrackingProperties: { flow_run_id: flowUuid },
      additionalUploadEventTrackingProperties: { flow_run_id: flowUuid },
    });
  };

  const handleButtonClick = (e: any) => {
    if (plpPhotoPreviewState?.uploadcareFile) {
      handleShowOptions(true, e);
    } else {
      startUcFlow();
    }
  };

  const handleWalkthroughAddPhotoClick = () => {
    flowUuid = uuidv4();
    isFromWalkthrough = true;
    trackEvent('Flow Started', getTrackingData());
    openUploadcareModal({
      ...uploadcareModalOptions,
      additionalUploadedFilesTrackingProperties: { flow_run_id: flowUuid },
      additionalUploadEventTrackingProperties: { flow_run_id: flowUuid },
    });
  };

  if (!plpPhotoPreviewState) {
    return null;
  }

  return (
    <Box mobile={mobile}>
      <ButtonBox mobile={mobile}>
        <ButtonV2
          id="preview-photo-walkthrough"
          fullWidth={mobile}
          onClick={(e: any) => handleButtonClick(e)}
        >
          {plpPhotoPreviewState.uploadcareFile ? (
            <Fragment>
              Photo Options <CaretIcon />
            </Fragment>
          ) : (
            <Fragment>Preview with photo</Fragment>
          )}
        </ButtonV2>

        {showOptions && (
          <Dropdown
            onSwap={startUcFlow}
            onDelete={() => {
              dispatch(updatePlpPhotoPreviewState({ uploadcareFile: null }));

              if (cardType) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { [cardType]: ignored, ...sessionData } = JSON.parse(
                  window.sessionStorage.getItem(ZOLA_PLP_PHOTO_PREVIEW) || '{}'
                );
                window.sessionStorage.setItem(ZOLA_PLP_PHOTO_PREVIEW, JSON.stringify(sessionData));
              }

              setShowOptions(false);
            }}
            onPhotoSave={handleDropdownSave}
          />
        )}

        {showPreviewPhotoWalkthrough && (
          <PreviewPhotoWalkthrough
            handleWalkthroughAddPhotoClick={handleWalkthroughAddPhotoClick}
            setShowPreviewPhotoWalkthrough={setShowPreviewPhotoWalkthrough}
          />
        )}
      </ButtonBox>
    </Box>
  );
};

export default Upload;
